'use client';

import { useState } from 'react';
import { useTheme } from 'next-themes';
import { VelocityTheme } from 'src/environmentVariables/EnvironmentVariables';
import { getMarketIconSrc } from 'src/components/Utils/MarketIcon';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

const COVALENT_LOGO_BASE = 'https://logos.covalenthq.com/tokens';
const IMAGE_PROXY = '/api/image-proxy';

// Track failed image URLs to avoid re-fetching (keeps only strings, minimal memory)
const failedImageUrls = new Set<string>();

function proxyExternalImageUrl(url: string): string {
	if (!url.startsWith('http://') && !url.startsWith('https://')) return url;
	return `${IMAGE_PROXY}?url=${encodeURIComponent(url)}`;
}

function getTokenIconUrl(chainId: string, address: string): string | null {
	// Covalent only supports EVM chains (0x + 40 hex). Skip Solana/base58 addresses.
	const isEvmAddress =
		(address.startsWith('0x') && address.length === 42) ||
		(!address.startsWith('0x') && address.length === 40);
	if (!isEvmAddress) return null;
	const addr = address.startsWith('0x')
		? address.toLowerCase()
		: `0x${address.toLowerCase()}`;
	return `${COVALENT_LOGO_BASE}/${chainId}/${addr}.png`;
}

interface LetterFallbackProps extends WithClassName {
	symbol: string;
}

function LetterFallback({ symbol, className }: LetterFallbackProps) {
	const letter = (symbol.slice(0, 2).toUpperCase() || '?').replace(/\d/g, '');
	return (
		<div
			className={cj(
				className,
				'bg-container-bg-secondary flex items-center justify-center rounded-full typo-body-2xs font-semibold text-text-secondary'
			)}
		>
			{letter}
		</div>
	);
}

interface FunXyzAssetIconProps extends WithClassName {
	logo?: string;
	symbol: string;
	chainId?: string;
	address?: string;
}

// Prefer Velocity's icons for assets we already have (ETH, HYPE, ARB, etc.); only use image proxy when needed.
export default function FunXyzAssetIcon({
	logo,
	symbol,
	chainId,
	address,
	className,
}: FunXyzAssetIconProps) {
	const theme = (useTheme().theme as VelocityTheme) ?? VelocityTheme.dark;
	const [velocityIconFailed, setVelocityIconFailed] = useState(false);
	const [proxyError, setProxyError] = useState(false);
	const tokenIconUrl =
		chainId && address ? getTokenIconUrl(chainId, address) : null;

	const velocityIconSrc = getMarketIconSrc({
		baseSymbol: symbol,
		theme,
		grey: false,
	});

	const rawProxySrc = logo ?? tokenIconUrl ?? null;
	const isProxyKnownFailed =
		(rawProxySrc !== null && failedImageUrls.has(rawProxySrc)) || proxyError;
	const proxyImgSrc =
		rawProxySrc !== null && !isProxyKnownFailed
			? proxyExternalImageUrl(rawProxySrc)
			: null;

	return (
		<div
			className={cj(
				'relative size-lg shrink-0 overflow-hidden rounded-full',
				className
			)}
		>
			<LetterFallback symbol={symbol} className={className} />
			{!velocityIconFailed && (
				<img
					src={velocityIconSrc}
					alt={symbol}
					className="absolute inset-0 size-full rounded-full object-cover"
					loading="lazy"
					decoding="async"
					onLoad={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onError={() => setVelocityIconFailed(true)}
					style={{ opacity: 0 }}
				/>
			)}
			{velocityIconFailed && proxyImgSrc && (
				<img
					src={proxyImgSrc}
					alt={symbol}
					className="absolute inset-0 size-full rounded-full object-cover"
					loading="lazy"
					decoding="async"
					referrerPolicy="no-referrer"
					onLoad={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onError={() => {
						if (rawProxySrc) {
							failedImageUrls.add(rawProxySrc);
							setProxyError(true);
						}
					}}
					style={{ opacity: 0 }}
				/>
			)}
		</div>
	);
}
